import type { SupportedLanguage } from '@/components/multilingual/LanguageSelector';

/**
 * Living sub-processor list.
 *
 * This page is deliberately NOT a versioned legal document: the Privacy Policy
 * describes purposes, data categories, recipient roles and locations in stable
 * wording, while the concrete providers live here. Swapping a provider inside
 * an already-disclosed category means editing this list and announcing it in
 * the changelog — a genuinely new category or a new third-country transfer
 * still requires a Privacy Policy update.
 */
export const SUBPROCESSOR_LIST_UPDATED = '2026-08-15';

export interface SubprocessorEntry {
  /** Stable key so translations stay in lockstep. */
  key: string;
  provider: string;
  purpose: Record<SupportedLanguage, string>;
  location: Record<SupportedLanguage, string>;
  data: Record<SupportedLanguage, string>;
  basis: Record<SupportedLanguage, string>;
  /** false = recipient without processor relationship (no personal data). */
  processor: boolean;
}

export const subprocessors: SubprocessorEntry[] = [
  {
    key: 'supabase',
    provider: 'Supabase Inc. (AWS)',
    processor: true,
    purpose: {
      en: 'Database, authentication, file storage, serverless functions',
      de: 'Datenbank, Authentifizierung, Dateispeicher, Serverless-Funktionen',
      fr: 'Base de données, authentification, stockage de fichiers, fonctions serverless',
      es: 'Base de datos, autenticación, almacenamiento de archivos, funciones serverless',
    },
    location: { en: 'EU/EEA (Ireland)', de: 'EU/EWR (Irland)', fr: 'UE/EEE (Irlande)', es: 'UE/EEE (Irlanda)' },
    data: {
      en: 'Account data, manuscripts, reviews, comments, consents, usage records',
      de: 'Kontodaten, Manuskripte, Reviews, Kommentare, Einwilligungen, Nutzungsdaten',
      fr: 'Données de compte, manuscrits, évaluations, commentaires, consentements, données d’usage',
      es: 'Datos de cuenta, manuscritos, revisiones, comentarios, consentimientos, datos de uso',
    },
    basis: {
      en: 'Art. 28 GDPR processing agreement — EU/EEA, no third-country transfer',
      de: 'Auftragsverarbeitung nach Art. 28 DSGVO — EU/EWR, kein Drittlandtransfer',
      fr: 'Sous-traitance au titre de l’art. 28 RGPD — UE/EEE, aucun transfert hors UE',
      es: 'Encargo de tratamiento (art. 28 RGPD) — UE/EEE, sin transferencia a terceros países',
    },
  },
  {
    key: 'vertex',
    provider: 'Google Cloud — Vertex AI (Gemini)',
    processor: true,
    purpose: {
      en: 'Generative AI: AI reviews, metadata extraction, Writer authoring turns',
      de: 'Generative KI: KI-Reviews, Metadatenextraktion, Schreib-Turns im Writer',
      fr: 'IA générative : évaluations IA, extraction de métadonnées, tours d’écriture du Writer',
      es: 'IA generativa: revisiones con IA, extracción de metadatos, turnos de escritura del Writer',
    },
    location: {
      en: 'EU multi-region (EU/EEA data centres only)',
      de: 'EU-Multi-Region (ausschließlich EU/EWR-Rechenzentren)',
      fr: 'Multi-région UE (centres de données UE/EEE uniquement)',
      es: 'Multirregión UE (solo centros de datos UE/EEE)',
    },
    data: {
      en: 'Manuscript text and prompts submitted for the requested AI action',
      de: 'Manuskripttexte und Prompts der jeweils angeforderten KI-Aktion',
      fr: 'Textes de manuscrit et prompts de l’action IA demandée',
      es: 'Texto del manuscrito y prompts de la acción de IA solicitada',
    },
    basis: {
      en: 'Google Cloud Data Processing Addendum (Art. 28 GDPR). Processing in the EU/EEA; in exceptional support cases access from third countries on the basis of EU standard contractual clauses. Content is not used to train models.',
      de: 'Google Cloud Data Processing Addendum (Art. 28 DSGVO). Verarbeitung in der EU/EWR; in Ausnahmefällen Support-Zugriff aus Drittländern auf Basis der EU-Standardvertragsklauseln. Inhalte werden nicht zum Modelltraining verwendet.',
      fr: 'Google Cloud Data Processing Addendum (art. 28 RGPD). Traitement dans l’UE/EEE ; accès exceptionnel du support depuis des pays tiers sur la base des clauses contractuelles types. Les contenus ne servent pas à l’entraînement des modèles.',
      es: 'Google Cloud Data Processing Addendum (art. 28 RGPD). Tratamiento en la UE/EEE; acceso excepcional de soporte desde terceros países sobre la base de las cláusulas contractuales tipo. Los contenidos no se usan para entrenar modelos.',
    },
  },
  {
    key: 'cloudrun',
    provider: 'Google Cloud Run — typesetting worker (Tectonic)',
    processor: true,
    purpose: {
      en: 'LaTeX compilation of Writer manuscripts into PDF',
      de: 'LaTeX-Kompilierung von Writer-Manuskripten zu PDF',
      fr: 'Compilation LaTeX des manuscrits du Writer en PDF',
      es: 'Compilación LaTeX de manuscritos del Writer a PDF',
    },
    location: {
      en: 'europe-west4 (Netherlands), EU/EEA',
      de: 'europe-west4 (Niederlande), EU/EWR',
      fr: 'europe-west4 (Pays-Bas), UE/EEE',
      es: 'europe-west4 (Países Bajos), UE/EEE',
    },
    data: {
      en: 'Manuscript source (LaTeX), bibliography, compile logs',
      de: 'Manuskript-Quelltext (LaTeX), Bibliografie, Kompilierungsprotokolle',
      fr: 'Source du manuscrit (LaTeX), bibliographie, journaux de compilation',
      es: 'Fuente del manuscrito (LaTeX), bibliografía, registros de compilación',
    },
    basis: {
      en: 'Google Cloud Data Processing Addendum (Art. 28 GDPR) — EU/EEA, no third-country transfer',
      de: 'Google Cloud Data Processing Addendum (Art. 28 DSGVO) — EU/EWR, kein Drittlandtransfer',
      fr: 'Google Cloud Data Processing Addendum (art. 28 RGPD) — UE/EEE, aucun transfert hors UE',
      es: 'Google Cloud Data Processing Addendum (art. 28 RGPD) — UE/EEE, sin transferencia a terceros países',
    },
  },
  {
    key: 'openalex',
    provider: 'OurResearch (OpenAlex)',
    processor: false,
    purpose: {
      en: 'Open bibliographic database queried for literature search and citation resolution',
      de: 'Offene bibliografische Datenbank für Literatursuche und Zitationsauflösung',
      fr: 'Base bibliographique ouverte interrogée pour la recherche documentaire et la résolution de citations',
      es: 'Base bibliográfica abierta consultada para búsqueda de literatura y resolución de citas',
    },
    location: { en: 'USA', de: 'USA', fr: 'États-Unis', es: 'EE. UU.' },
    data: {
      en: 'A sanitised topical search term only. Requests are made by our EU servers, never by your browser; identity, IP address, e-mail addresses, affiliations and manuscript passages are removed before the request leaves the EU.',
      de: 'Ausschließlich ein bereinigter thematischer Suchbegriff. Die Anfrage stellen unsere EU-Server, nie Ihr Browser; Identität, IP-Adresse, E-Mail-Adressen, Affiliationen und Manuskriptpassagen werden vor dem Verlassen der EU entfernt.',
      fr: 'Uniquement un terme de recherche thématique nettoyé. Les requêtes partent de nos serveurs UE, jamais de votre navigateur ; identité, adresse IP, adresses e-mail, affiliations et passages du manuscrit sont supprimés avant le départ de l’UE.',
      es: 'Solo un término de búsqueda temático depurado. Las peticiones parten de nuestros servidores en la UE, nunca de su navegador; identidad, dirección IP, correos, afiliaciones y pasajes del manuscrito se eliminan antes de salir de la UE.',
    },
    basis: {
      en: 'No processor relationship: no personal data is transmitted, so Art. 44 ff. GDPR is not engaged. The property is enforced in code and covered by automated tests.',
      de: 'Kein Auftragsverarbeitungsverhältnis: Es werden keine personenbezogenen Daten übermittelt, Art. 44 ff. DSGVO ist daher nicht einschlägig. Die Eigenschaft ist technisch erzwungen und durch automatisierte Tests abgesichert.',
      fr: 'Aucune relation de sous-traitance : aucune donnée personnelle n’est transmise, les art. 44 et s. RGPD ne s’appliquent donc pas. Cette propriété est imposée par le code et couverte par des tests automatisés.',
      es: 'Sin relación de encargo: no se transmiten datos personales, por lo que no se aplican los arts. 44 y ss. RGPD. La propiedad se impone en el código y está cubierta por pruebas automatizadas.',
    },
  },
  {
    key: 'paddle',
    provider: 'Paddle.com Market Ltd.',
    processor: false,
    purpose: {
      en: 'Payment processing and invoicing as Merchant of Record',
      de: 'Zahlungsabwicklung und Rechnungsstellung als Merchant of Record',
      fr: 'Traitement des paiements et facturation en tant que Merchant of Record',
      es: 'Procesamiento de pagos y facturación como Merchant of Record',
    },
    location: { en: 'United Kingdom / USA', de: 'Vereinigtes Königreich / USA', fr: 'Royaume-Uni / États-Unis', es: 'Reino Unido / EE. UU.' },
    data: {
      en: 'Account identifier, billing details, transaction and tax data',
      de: 'Kontokennung, Rechnungsdaten, Transaktions- und Steuerdaten',
      fr: 'Identifiant de compte, données de facturation, données de transaction et fiscales',
      es: 'Identificador de cuenta, datos de facturación, datos de transacción y fiscales',
    },
    basis: {
      en: 'Own controller for payment data; UK adequacy decision and EU standard contractual clauses',
      de: 'Eigenverantwortlicher für Zahlungsdaten; UK-Angemessenheitsbeschluss und EU-Standardvertragsklauseln',
      fr: 'Responsable de traitement propre pour les données de paiement ; décision d’adéquation UK et CCT',
      es: 'Responsable propio de los datos de pago; decisión de adecuación del Reino Unido y cláusulas tipo',
    },
  },
  {
    key: 'resend',
    provider: 'Resend, Inc.',
    processor: true,
    purpose: {
      en: 'Transactional e-mail (confirmations, notifications, invitations)',
      de: 'Transaktions-E-Mails (Bestätigungen, Benachrichtigungen, Einladungen)',
      fr: 'E-mails transactionnels (confirmations, notifications, invitations)',
      es: 'Correos transaccionales (confirmaciones, notificaciones, invitaciones)',
    },
    location: { en: 'USA', de: 'USA', fr: 'États-Unis', es: 'EE. UU.' },
    data: {
      en: 'Recipient e-mail address, name, message content',
      de: 'E-Mail-Adresse, Name, Nachrichteninhalt der Empfänger',
      fr: 'Adresse e-mail du destinataire, nom, contenu du message',
      es: 'Dirección de correo del destinatario, nombre, contenido del mensaje',
    },
    basis: {
      en: 'Art. 28 GDPR processing agreement with EU standard contractual clauses',
      de: 'Auftragsverarbeitung nach Art. 28 DSGVO mit EU-Standardvertragsklauseln',
      fr: 'Sous-traitance (art. 28 RGPD) avec clauses contractuelles types',
      es: 'Encargo de tratamiento (art. 28 RGPD) con cláusulas contractuales tipo',
    },
  },
  {
    key: 'lovable',
    provider: 'Lovable Labs Inc.',
    processor: true,
    purpose: {
      en: 'Frontend hosting and content delivery',
      de: 'Frontend-Hosting und Auslieferung (CDN)',
      fr: 'Hébergement du frontend et diffusion de contenu',
      es: 'Alojamiento del frontend y entrega de contenido',
    },
    location: { en: 'USA', de: 'USA', fr: 'États-Unis', es: 'EE. UU.' },
    data: {
      en: 'Technical connection data (IP address, user agent, server logs)',
      de: 'Technische Verbindungsdaten (IP-Adresse, User-Agent, Server-Logs)',
      fr: 'Données techniques de connexion (adresse IP, user-agent, journaux serveur)',
      es: 'Datos técnicos de conexión (dirección IP, user agent, registros de servidor)',
    },
    basis: {
      en: 'Art. 28 GDPR processing agreement with EU standard contractual clauses (no DPF certification)',
      de: 'Auftragsverarbeitung nach Art. 28 DSGVO mit EU-Standardvertragsklauseln (keine DPF-Zertifizierung)',
      fr: 'Sous-traitance (art. 28 RGPD) avec clauses contractuelles types (pas de certification DPF)',
      es: 'Encargo de tratamiento (art. 28 RGPD) con cláusulas tipo (sin certificación DPF)',
    },
  },
  {
    key: 'searchengines',
    provider: 'Google LLC / Microsoft Corp. (indexing APIs)',
    processor: false,
    purpose: {
      en: 'Submitting URLs of publicly published papers for indexing',
      de: 'Übermittlung öffentlich veröffentlichter Paper-URLs zur Indexierung',
      fr: 'Soumission des URL des articles publiés pour indexation',
      es: 'Envío de URL de artículos publicados para su indexación',
    },
    location: { en: 'USA', de: 'USA', fr: 'États-Unis', es: 'EE. UU.' },
    data: {
      en: 'Public paper URLs only',
      de: 'Ausschließlich öffentliche Paper-URLs',
      fr: 'Uniquement des URL publiques d’articles',
      es: 'Solo URL públicas de artículos',
    },
    basis: {
      en: 'EU-US Data Privacy Framework; only already-public URLs are transmitted',
      de: 'EU-US Data Privacy Framework; übermittelt werden nur bereits öffentliche URLs',
      fr: 'EU-US Data Privacy Framework ; seules des URL déjà publiques sont transmises',
      es: 'EU-US Data Privacy Framework; solo se transmiten URL ya públicas',
    },
  },
];

export const subprocessorsCopy: Record<
  SupportedLanguage,
  {
    breadcrumb: string;
    title: string;
    intro: string;
    updated: string;
    howWeChange: string;
    howWeChangeBody: string;
    colProvider: string;
    colPurpose: string;
    colLocation: string;
    colData: string;
    colBasis: string;
    processorTag: string;
    recipientTag: string;
    contact: string;
    backToPrivacy: string;
  }
> = {
  en: {
    breadcrumb: 'Sub-processors',
    title: 'Sub-processors and recipients',
    intro:
      'This section lists every service provider that may process personal data or manuscript content on our behalf, together with the location of the processing and the legal basis. It complements the Privacy Policy, which describes the purposes and data categories in binding form.',
    updated: 'Last updated',
    howWeChange: 'How we change this list',
    howWeChangeBody:
      'We may add or replace a provider within a category that is already disclosed in the Privacy Policy; such changes are made here and announced in the update notice on the Privacy Policy. A new processing category or a new third-country transfer always requires an updated Privacy Policy.',
    colProvider: 'Provider',
    colPurpose: 'Purpose',
    colLocation: 'Processing location',
    colData: 'Data processed',
    colBasis: 'Role and legal basis',
    processorTag: 'Processor (Art. 28 GDPR)',
    recipientTag: 'Recipient — no processing agreement',
    contact: 'Questions about this list: contact@paperverse.io',
    backToPrivacy: 'Back to the Privacy Policy',
  },
  de: {
    breadcrumb: 'Unterauftragsverarbeiter',
    title: 'Unterauftragsverarbeiter und Empfänger',
    intro:
      'Dieser Abschnitt listet alle Dienstleister auf, die in unserem Auftrag personenbezogene Daten oder Manuskriptinhalte verarbeiten können, jeweils mit Verarbeitungsort und Rechtsgrundlage. Sie ergänzt die Datenschutzerklärung, die Zwecke und Datenkategorien verbindlich beschreibt.',
    updated: 'Zuletzt aktualisiert',
    howWeChange: 'Wie wir diese Liste ändern',
    howWeChangeBody:
      'Innerhalb einer bereits in der Datenschutzerklärung offengelegten Kategorie können wir Anbieter ergänzen oder austauschen; solche Änderungen erfolgen hier und werden im Aktualisierungshinweis der Datenschutzerklärung bekannt gegeben. Eine neue Verarbeitungskategorie oder ein neuer Drittlandtransfer erfordert stets eine aktualisierte Datenschutzerklärung.',
    colProvider: 'Anbieter',
    colPurpose: 'Zweck',
    colLocation: 'Verarbeitungsort',
    colData: 'Verarbeitete Daten',
    colBasis: 'Rolle und Rechtsgrundlage',
    processorTag: 'Auftragsverarbeiter (Art. 28 DSGVO)',
    recipientTag: 'Empfänger — keine Auftragsverarbeitung',
    contact: 'Fragen zu dieser Liste: contact@paperverse.io',
    backToPrivacy: 'Zurück zur Datenschutzerklärung',
  },
  fr: {
    breadcrumb: 'Sous-traitants',
    title: 'Sous-traitants et destinataires',
    intro:
      'Cette section recense tous les prestataires susceptibles de traiter des données personnelles ou des contenus de manuscrit pour notre compte, avec le lieu de traitement et la base légale. Elle complète la politique de confidentialité, qui décrit les finalités et catégories de données de manière contraignante.',
    updated: 'Dernière mise à jour',
    howWeChange: 'Comment nous modifions cette liste',
    howWeChangeBody:
      'Nous pouvons ajouter ou remplacer un prestataire au sein d’une catégorie déjà divulguée dans la politique de confidentialité ; ces modifications sont faites ici et annoncées dans l’avis de mise à jour. Une nouvelle catégorie de traitement ou un nouveau transfert hors UE nécessite toujours une politique de confidentialité actualisée.',
    colProvider: 'Prestataire',
    colPurpose: 'Finalité',
    colLocation: 'Lieu de traitement',
    colData: 'Données traitées',
    colBasis: 'Rôle et base légale',
    processorTag: 'Sous-traitant (art. 28 RGPD)',
    recipientTag: 'Destinataire — pas de sous-traitance',
    contact: 'Questions sur cette liste : contact@paperverse.io',
    backToPrivacy: 'Retour à la politique de confidentialité',
  },
  es: {
    breadcrumb: 'Encargados',
    title: 'Encargados del tratamiento y destinatarios',
    intro:
      'Esta sección enumera todos los proveedores que pueden tratar datos personales o contenidos de manuscritos por cuenta nuestra, con el lugar del tratamiento y la base jurídica. Complementa la política de privacidad, que describe de forma vinculante las finalidades y categorías de datos.',
    updated: 'Última actualización',
    howWeChange: 'Cómo modificamos esta lista',
    howWeChangeBody:
      'Podemos añadir o sustituir un proveedor dentro de una categoría ya divulgada en la política de privacidad; dichos cambios se realizan aquí y se anuncian en el aviso de actualización. Una nueva categoría de tratamiento o una nueva transferencia a terceros países exige siempre una política de privacidad actualizada.',
    colProvider: 'Proveedor',
    colPurpose: 'Finalidad',
    colLocation: 'Lugar del tratamiento',
    colData: 'Datos tratados',
    colBasis: 'Función y base jurídica',
    processorTag: 'Encargado (art. 28 RGPD)',
    recipientTag: 'Destinatario — sin encargo de tratamiento',
    contact: 'Consultas sobre esta lista: contact@paperverse.io',
    backToPrivacy: 'Volver a la política de privacidad',
  },
};
